import {
  ChangeEvent,
  forwardRef,
  ReactNode,
  Ref,
  useEffect,
  useState,
} from 'react';

import { ErrorMessage, HelpText, InputLabel } from '@betrybe/ada-react';
import { Radio } from '@/components/atoms';

type optionProps = {
  value: string;
  label: string;
};

type radioInputProps = {
  disabled: boolean;
  errorMessage: ReactNode;
  helpText: ReactNode;
  id: string;
  label: string;
  name: string;
  onChange: (event: ChangeEvent<HTMLInputElement>) => void;
  options: optionProps[];
  selected: string;
};

export default forwardRef(function RadioInput(
  props: radioInputProps,
  ref: Ref<HTMLInputElement>,
) {
  const {
    disabled,
    errorMessage,
    helpText,
    id,
    label,
    name,
    onChange,
    options,
    selected,
    ...radioProps
  } = props;

  const [currentSelected, setCurrentSelected] = useState(selected);

  useEffect(() => setCurrentSelected(selected), [selected]);

  const radioGroupId = `radio-group-${id}`;
  const radioGroupLabelId = `radio-group-label-${id}`;

  const handleOnChange = (event: ChangeEvent<HTMLInputElement>) => {
    setCurrentSelected(event.target.value);
    if (onChange) {
      onChange(event);
    }
  };

  const renderOption = (radio: optionProps) => {
    const radioId = `radio-${name}-${radio.value}`;
    const radioLabelId = `radio-label-${name}-${radio.value}`;

    return (
      <InputLabel className="radio-label" key={radioId} inputId={radioId}>
        <Radio
          disabled={disabled}
          id={radioId}
          name={name}
          onChange={handleOnChange}
          ref={ref}
          selected={currentSelected === radio.value}
          value={radio.value}
          aria-label={radio.label}
          aria-labelledby={radioLabelId}
          {...radioProps}
        />
        <span id={radioLabelId} className="text">
          {radio.label}
        </span>
      </InputLabel>
    );
  };

  return (
    <div className="flex flex-col">
      <InputLabel id={radioGroupLabelId} inputId={radioGroupId}>
        {label}
      </InputLabel>
      <HelpText id={id}>{helpText}</HelpText>
      <div
        id={radioGroupId}
        role="radiogroup"
        className="radio-group"
        aria-label={label}
        aria-labelledby={radioGroupLabelId}
      >
        {options.map(renderOption)}
      </div>
      <ErrorMessage>{errorMessage}</ErrorMessage>
    </div>
  );
});
